import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import api, { fmtQty } from '../../utils/api';

const CONTAINER_40HC_CBM = 67;  // no-pallet capacity
const fmtWeight = (kg) => kg >= 1000 ? `${(kg / 1000).toFixed(2)}t` : `${kg.toFixed(1)} kg`;

export default function ContainerSimulator() {
  const { t } = useTranslation();
  const he = document.documentElement.dir === 'rtl';

  const [scenarios, setScenarios] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [current, setCurrent] = useState(null); // { simulation, items }
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(null); // { sku, qty }

  const fetchScenarios = useCallback(async () => {
    try { const r = await api.get('/logistics/simulator'); setScenarios(r.data.simulations || []); }
    catch (e) { /* swallow */ }
  }, []);

  const fetchProducts = useCallback(async () => {
    try { const r = await api.get('/warehouse/stock-summary'); setProducts(r.data || []); }
    catch (e) { /* swallow */ }
  }, []);

  const fetchCurrent = useCallback(async (id) => {
    if (!id) { setCurrent(null); return; }
    try { const r = await api.get(`/logistics/simulator/${id}`); setCurrent(r.data); }
    catch (e) { setCurrent(null); }
  }, []);

  useEffect(() => { fetchScenarios(); fetchProducts(); }, [fetchScenarios, fetchProducts]);
  useEffect(() => { fetchCurrent(currentId); }, [currentId, fetchCurrent]);

  const createScenario = async () => {
    const name = newName.trim();
    if (!name) { alert(he ? 'שם נדרש' : 'Name required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/logistics/simulator', { name });
      setNewName('');
      await fetchScenarios();
      setCurrentId(r.data.simulation.id);
    } catch (e) { alert((he ? 'שגיאה: ' : 'Error: ') + (e.response?.data?.error || e.message)); }
    finally { setBusy(false); }
  };

  const addItem = async (sku, quantity) => {
    if (!currentId) return;
    const q = parseInt(quantity, 10);
    if (!q || q <= 0) return;
    try {
      await api.post(`/logistics/simulator/${currentId}/items`, { sku, quantity: q });
      setAdding(null);
      await fetchCurrent(currentId);
    } catch (e) { alert((he ? 'שגיאה: ' : 'Error: ') + (e.response?.data?.error || e.message)); }
  };

  const removeItem = async (itemId) => {
    if (!currentId) return;
    try {
      await api.delete(`/logistics/simulator/${currentId}/items/${itemId}`);
      await fetchCurrent(currentId);
    } catch (e) { alert((he ? 'שגיאה: ' : 'Error: ') + (e.response?.data?.error || e.message)); }
  };

  const updateQty = async (item, newQty) => {
    const q = parseInt(newQty, 10);
    if (!q || q <= 0) { await removeItem(item.id); return; }
    await addItem(item.sku, q); // POST is UPSERT by (simulation_id, sku)
  };

  const deleteScenario = async () => {
    if (!currentId) return;
    if (!window.confirm(he ? 'למחוק את התרחיש?' : 'Delete scenario?')) return;
    try {
      await api.delete(`/logistics/simulator/${currentId}`);
      setCurrentId(null);
      await fetchScenarios();
    } catch (e) { alert((he ? 'שגיאה: ' : 'Error: ') + (e.response?.data?.error || e.message)); }
  };

  const clearItems = async () => {
    if (!currentId) return;
    if (!window.confirm(he ? 'לנקות את כל הפריטים?' : 'Clear all items?')) return;
    try {
      await api.post(`/logistics/simulator/${currentId}/clear`);
      await fetchCurrent(currentId);
    } catch (e) { alert((he ? 'שגיאה: ' : 'Error: ') + (e.response?.data?.error || e.message)); }
  };

  const exportExcel = () => {
    if (!current?.items?.length) return;
    const rows = current.items.map(i => ({
      'SKU': i.sku,
      'Product': i.description,
      'Barcode': i.barcode || '',
      'Qty': i.quantity,
      'CBM/unit': i.cbm_per_unit,
      'Line CBM': Number(i.line_cbm).toFixed(3),
      'Weight kg/unit': i.weight_kg,
      'Line weight kg': Number(i.line_weight).toFixed(1),
    }));
    const totalCbm = current.items.reduce((s, i) => s + (i.line_cbm || 0), 0);
    const totalKg = current.items.reduce((s, i) => s + (i.line_weight || 0), 0);
    const totalQty = current.items.reduce((s, i) => s + (i.quantity || 0), 0);
    rows.push({ SKU: '', Product: 'TOTAL', Barcode: '', Qty: totalQty, 'CBM/unit': '', 'Line CBM': totalCbm.toFixed(3), 'Weight kg/unit': '', 'Line weight kg': totalKg.toFixed(1) });
    const fillPct = (totalCbm / CONTAINER_40HC_CBM) * 100;
    rows.push({});
    rows.push({ SKU: '', Product: `Container 40HC = ${CONTAINER_40HC_CBM} CBM (no pallets)`, Barcode: '', Qty: '', 'CBM/unit': '', 'Line CBM': '', 'Weight kg/unit': 'Fill %:', 'Line weight kg': fillPct.toFixed(1) + '%' });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{wch:12},{wch:38},{wch:16},{wch:10},{wch:12},{wch:12},{wch:14},{wch:14}];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Container Sim');
    const safeName = (current.simulation.name || 'sim').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '-');
    XLSX.writeFile(wb, `container-sim-${safeName}.xlsx`);
  };

  // Filtered product picker
  const sLower = search.toLowerCase().trim();
  const filtered = products.filter(p =>
    !sLower ||
    (p.description || '').toLowerCase().includes(sLower) ||
    (p.sku || '').toLowerCase().includes(sLower) ||
    (p.barcodes || []).some(b => String(b).toLowerCase().includes(sLower)) ||
    (p.aliasSkus || []).some(s => String(s).toLowerCase().includes(sLower))
  );

  // Totals (live)
  const items = current?.items || [];
  const totalCbm = items.reduce((s, i) => s + (i.line_cbm || 0), 0);
  const totalWeight = items.reduce((s, i) => s + (i.line_weight || 0), 0);
  const totalQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const fillPct = (totalCbm / CONTAINER_40HC_CBM) * 100;
  const containers = totalCbm > 0 ? Math.ceil(totalCbm / CONTAINER_40HC_CBM) : 0;
  const fillColor = fillPct > 100 ? '#dc2626' : fillPct > 80 ? '#d97706' : '#16a34a';
  const fillBg = fillPct > 100 ? '#fee2e2' : fillPct > 80 ? '#fef3c7' : '#dcfce7';

  return (
    <div>
      <h2 style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 700, letterSpacing: 1, marginBottom: 8 }}>
        📦 {he ? 'סימולטור קונטיינר' : 'Container Simulator'}
        <span style={{ fontSize: 12, fontWeight: 400, color: '#6c757d', marginInlineStart: 12 }}>
          (40HC — {CONTAINER_40HC_CBM} CBM, {he ? 'ללא פלטות' : 'no pallets'})
        </span>
      </h2>
      <div className="text-muted" style={{ fontSize: 12, marginBottom: 16 }}>
        {he ? 'תכנן עומס לקונטיינר בלי להשפיע על המלאי. שמור כתרחישים ובחר אחר־כך.' : 'Plan container loads without affecting stock. Save scenarios to compare.'}
      </div>

      {/* Scenario controls */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="input" value={currentId || ''} onChange={e => setCurrentId(e.target.value ? Number(e.target.value) : null)} style={{ minWidth: 240 }}>
          <option value="">{he ? '— בחר תרחיש —' : '— Select scenario —'}</option>
          {scenarios.map(s => (
            <option key={s.id} value={s.id}>{s.name} ({s.item_count} {he ? 'פריטים' : 'items'})</option>
          ))}
        </select>
        <input className="input" placeholder={he ? 'שם תרחיש חדש' : 'New scenario name'} value={newName} onChange={e => setNewName(e.target.value)} style={{ maxWidth: 220 }}
          onKeyDown={e => { if (e.key === 'Enter') createScenario(); }} />
        <button className="btn btn-gold btn-sm" onClick={createScenario} disabled={busy || !newName.trim()}>{he ? '+ חדש' : '+ New'}</button>
        {currentId && (<>
          <div style={{ flex: 1 }} />
          <button className="btn btn-outline btn-sm" onClick={clearItems} disabled={!items.length}>{he ? '🧹 נקה פריטים' : '🧹 Clear items'}</button>
          <button className="btn btn-outline btn-sm" onClick={exportExcel} disabled={!items.length}>📥 Excel</button>
          <button className="btn btn-outline btn-sm" style={{ color: '#dc2626', borderColor: '#dc2626' }} onClick={deleteScenario}>🗑 {he ? 'מחק תרחיש' : 'Delete'}</button>
        </>)}
      </div>

      {!currentId ? (
        <div className="empty-state" style={{ padding: 32, textAlign: 'center', color: '#6c757d' }}>
          {he ? 'בחר תרחיש קיים או צור חדש כדי להתחיל' : 'Select an existing scenario or create a new one to start'}
        </div>
      ) : (
        <>
          {/* Live totals */}
          <div className="card" style={{ marginBottom: 16, padding: 16, background: fillBg, border: `1px solid ${fillColor}` }}>
            <div style={{ display: 'flex', gap: 32, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <div>
                <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>CBM</span>{' '}
                <strong style={{ fontSize: 28, fontFamily: "'DM Mono', monospace" }}>{totalCbm.toFixed(2)}</strong>
                <span style={{ color: '#6c757d', marginInlineStart: 4 }}>/ {CONTAINER_40HC_CBM}</span>
              </div>
              <div>
                <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>{he ? 'מילוי' : 'Fill'}</span>{' '}
                <strong style={{ fontSize: 28, color: fillColor, fontFamily: "'DM Mono', monospace" }}>{fillPct.toFixed(1)}%</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>{he ? 'קונטיינרים' : 'Containers'}</span>{' '}
                <strong style={{ fontSize: 24, fontFamily: "'DM Mono', monospace" }}>{containers}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>{he ? 'יחידות' : 'Units'}</span>{' '}
                <strong style={{ fontSize: 18 }}>{fmtQty(totalQty)}</strong>
              </div>
              <div>
                <span style={{ fontSize: 11, color: '#6c757d', textTransform: 'uppercase' }}>{he ? 'משקל' : 'Weight'}</span>{' '}
                <strong style={{ fontSize: 16 }}>{totalWeight > 0 ? fmtWeight(totalWeight) : '—'}</strong>
              </div>
            </div>
            {/* Fill bar */}
            <div style={{ marginTop: 12, height: 10, background: '#fff', borderRadius: 4, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              <div style={{ width: `${Math.min(100, fillPct)}%`, height: '100%', background: fillColor, transition: 'width 0.3s' }} />
            </div>
          </div>

          {/* Selected items table */}
          {items.length > 0 ? (
            <div className="table-wrap" style={{ marginBottom: 24 }}>
              <table>
                <thead><tr>
                  <th>{t('common.sku')}</th>
                  <th>{t('common.product')}</th>
                  <th className="text-end">{he ? 'כמות' : 'Qty'}</th>
                  <th className="text-end">CBM/u</th>
                  <th className="text-end">CBM</th>
                  <th className="text-end">{he ? 'משקל' : 'Weight'}</th>
                  <th style={{ width: 40 }}></th>
                </tr></thead>
                <tbody>
                  {items.map(i => (
                    <tr key={i.id}>
                      <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                        {i.sku}
                        {i.barcode && <div className="text-muted" style={{ fontSize: 10 }}>{i.barcode}</div>}
                      </td>
                      <td>{i.description}</td>
                      <td className="text-end">
                        <input
                          className="inline-edit"
                          type="number"
                          min="1"
                          defaultValue={i.quantity}
                          style={{ width: 80, textAlign: 'right' }}
                          onBlur={e => { const v = parseInt(e.target.value, 10); if (v && v !== i.quantity) updateQty(i, v); }}
                          onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                        />
                      </td>
                      <td className="text-end text-muted" style={{ fontSize: 11 }}>{i.cbm_per_unit ? Number(i.cbm_per_unit).toFixed(4) : '—'}</td>
                      <td className="text-end fw-bold">{i.line_cbm ? Number(i.line_cbm).toFixed(2) : '—'}</td>
                      <td className="text-end" style={{ fontSize: 12 }}>{i.line_weight ? fmtWeight(Number(i.line_weight)) : '—'}</td>
                      <td>
                        <button className="btn btn-sm" style={{ color: '#dc2626', padding: '2px 8px', background: 'transparent', border: '1px solid #dc2626' }} onClick={() => removeItem(i.id)} title={he ? 'הסר' : 'Remove'}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state" style={{ padding: 20, textAlign: 'center', color: '#6c757d', marginBottom: 16 }}>
              {he ? 'אין פריטים — חפש והוסף למטה' : 'No items — search and add below'}
            </div>
          )}

          {/* Product picker */}
          <div className="card" style={{ padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', color: '#6c757d' }}>
              {he ? 'הוסף פריט' : 'Add Item'}
            </h3>
            <input
              className="input"
              placeholder={'🔍 ' + (he ? 'חפש לפי שם / SKU / ברקוד' : 'Search by name / SKU / barcode')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ marginBottom: 12, maxWidth: 400 }}
            />
            {sLower && (
              <div className="table-wrap" style={{ maxHeight: 360, overflow: 'auto' }}>
                <table>
                  <thead><tr>
                    <th>{t('common.sku')}</th>
                    <th>{t('common.product')}</th>
                    <th className="text-end">CBM/u</th>
                    <th className="text-end">{he ? 'במלאי' : 'In stock'}</th>
                    <th>{he ? 'כמות' : 'Qty'}</th>
                    <th style={{ width: 40 }}></th>
                  </tr></thead>
                  <tbody>
                    {filtered.slice(0, 50).map(p => {
                      const isAdding = adding && adding.sku === p.sku;
                      return (
                        <tr key={p.sku}>
                          <td style={{ fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                            {p.sku}
                            {p.barcodes && p.barcodes.length > 0 && (
                              <div className="text-muted" style={{ fontSize: 10 }}>{p.barcodes.join(' · ')}</div>
                            )}
                          </td>
                          <td>{p.description}</td>
                          <td className="text-end text-muted" style={{ fontSize: 11 }}>
                            {p.cbmPerUnit ? Number(p.cbmPerUnit).toFixed(4) : <span style={{ color: '#dc2626' }} title={he ? 'אין נתון CBM' : 'No CBM data'}>—</span>}
                          </td>
                          <td className="text-end text-muted" style={{ fontSize: 11 }}>{fmtQty(p.totalAvailable || 0)}</td>
                          <td>
                            <input
                              className="inline-edit"
                              type="number"
                              min="1"
                              style={{ width: 80 }}
                              value={isAdding ? adding.qty : ''}
                              placeholder="0"
                              onChange={e => setAdding({ sku: p.sku, qty: e.target.value })}
                              onKeyDown={e => { if (e.key === 'Enter' && isAdding && parseInt(adding.qty, 10) > 0) addItem(p.sku, adding.qty); }}
                            />
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-gold"
                              disabled={!isAdding || !parseInt(adding.qty, 10)}
                              onClick={() => addItem(p.sku, adding.qty)}
                            >+</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filtered.length > 50 && (
                  <div className="text-muted" style={{ fontSize: 11, marginTop: 6, padding: '4px 8px' }}>
                    {he ? `+${filtered.length - 50} עוד — חדד חיפוש` : `+${filtered.length - 50} more — refine search`}
                  </div>
                )}
                {filtered.length === 0 && (
                  <div className="text-muted" style={{ fontSize: 12, padding: 12, textAlign: 'center' }}>
                    {he ? 'לא נמצאו תוצאות' : 'No matches'}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
